import type { fabric } from 'fabric';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  Subject,
  Subscription,
} from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BoardItem } from '../sessions/session/items-canvas/board-items/board.item';
import { FabricObjectState } from '../sessions/session/wb-canvas/fabric-context-menu/fabric-context-menu.component';
import {
  isBoardFormula,
  isBoardImage,
  isBoardImagesPdf,
  isBoardLine,
  isBoardStickyNote,
  isBoardStickyNoteTextBox,
  isBoardVideo,
} from '../sessions/common/y-session';
import { isShape, itemIsScribbleOrHighlight } from '../sessions/session/wb-canvas/fabric-utils';
import { BoardItemsInteractionsManager } from '../sessions/session/items-canvas/board-items-interactions-manager';
import { ItemModel } from '../models/itemModel';
import { ProviderStateService } from './provider-state.service';
import { SessionSharedDataService, SessionView } from './session-shared-data.service';
import { FLAGS, FlagsService } from './flags.service';
import { PanelLayoutService } from './panel-layout.service';

enum MenuType {
  TEXTBOX = 'textbox',
  STICKY_NOTE = 'sticky_note',
  SHAPE = 'shape',
  LINE = 'line',
  SCRIBBLE = 'scribble',
  FORMULA = 'formula',
  LOCKED = 'locked',
  MULTI_SELECTION = 'multi_selection',
}

// not including the Comment button
const MENU_WIDTH_BY_TYPE = {
  [MenuType.TEXTBOX]: 742,
  [MenuType.STICKY_NOTE]: 638,
  [MenuType.SHAPE]: 354,
  [MenuType.LINE]: 354,
  [MenuType.SCRIBBLE]: 304,
  [MenuType.FORMULA]: 482,
  [MenuType.LOCKED]: 40,
  [MenuType.MULTI_SELECTION]: 40,
};

const CONTEXT_MENU_HEIGHT = 70;
const CONTEXT_MENU_RENDER_TOP_OFFSET = 60;
const CONTEXT_MENU_RENDER_BOTTOM_OFFSET = 20;
const BOTTOM_MARGIN = 190; // this values depends on bottom bar height
const COLOR_PICKER_HEIGHT = 400;
const COMMENT_MENU_WIDTH = 40;
const TOP_NAV_BAR_HEIGHT = 48;
const LEFT_PANEL_ICONS_WIDTH = 55;
const COLOR_PICKER_RENDER_BOTTOM_OFFSET = 50;
const MENU_MARGIN_FROM_VCT = 10;
const EXTRA_MARGIN_FOR_MENU_TRANSLATE_IN_PERCENT = 2;
const MENU_OFFSET_FROM_IMAGE_PDF = 30;

interface ObjectCenterMappedToScreen {
  x: number;
  y: number;
}

interface ObjectDataMappedToScreen {
  center: ObjectCenterMappedToScreen;
  rotatedWidth: number;
  rotatedHeight: number;
}

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
/**
 * The ContextMenuManagerService is responsible for managing the context menu position for all the items on the canvas.
 * We prioritize to render the context menu above the object, but if no enough space above the object, we render it below the object.
 * The same for sub menus and color picker positions.
 * The calculations takes into account
 * - the current zoom level
 * - the position of the rotation control icon.
 * - the position of the left panel icons.
 * - the position of the video call tower.
 */
export class ContextMenuManagerService {
  private videoCallTowerResizeObserver?: ResizeObserver;
  private videoCallTowerContainer: HTMLElement | null = null;
  private leftPanelIconsContainer: HTMLElement | null = null;

  private _requestFabricContextMenuUpdate = new Subject<void>();
  public requestFabricContextMenuUpdate$ = this._requestFabricContextMenuUpdate.asObservable();

  private isMenuRenderedOnTop = true;
  private lastContextMenuTopY: number = 0;
  readonly menuTypeToWidth: Record<MenuType, number>;

  private videoCallTowerSubscription = new Subscription();

  // Emits the computed CSS top value for the color picker
  private colorPickerTopSubject = new BehaviorSubject<string>(`-${COLOR_PICKER_HEIGHT}px`);
  public readonly colorPickerTop$ = this.colorPickerTopSubject.asObservable();
  public readonly isContextMenuRendered$ = this.boardItemsInteractionsManager.selection$.pipe(
    map((selectionState) => Boolean(selectionState)),
    distinctUntilChanged(),
  );

  constructor(
    private sharedDataService: SessionSharedDataService,
    private providerStateService: ProviderStateService,
    private flagService: FlagsService,
    private boardItemsInteractionsManager: BoardItemsInteractionsManager,
    private panelLayoutService: PanelLayoutService,
  ) {
    this.menuTypeToWidth = MENU_WIDTH_BY_TYPE;
  }

  public init(): void {
    this.videoCallTowerResizeObserver = new ResizeObserver(() => {
      this._requestFabricContextMenuUpdate.next();
    });

    this.videoCallTowerSubscription = combineLatest([
      this.sharedDataService.sessionView.current$,
      this.providerStateService.callConnected$,
    ])
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(
          ([prevView, prevCall], [view, call]) => prevView.view === view.view && prevCall === call,
        ),
      )
      .subscribe(([sessionView, callConnected]) => {
        this.videoCallTowerContainer = this.getVCTowerContainerEl();
        if (!this.videoCallTowerContainer) {
          return;
        }
        if (sessionView.view === SessionView.WHITEBOARD && callConnected) {
          this.videoCallTowerResizeObserver?.observe(this.videoCallTowerContainer);
        } else {
          this._requestFabricContextMenuUpdate.next();
          this.videoCallTowerResizeObserver?.unobserve(this.videoCallTowerContainer);
        }
      });
  }

  private getVCTowerContainerEl() {
    if (!this.videoCallTowerContainer || !this.videoCallTowerContainer.isConnected) {
      this.videoCallTowerContainer = document.getElementById(
        'overlay-parent-container',
      ) as HTMLElement | null;
    }
    return this.videoCallTowerContainer;
  }

  private getLeftPanelIconsContainerEl() {
    if (!this.leftPanelIconsContainer || !this.leftPanelIconsContainer.isConnected) {
      this.leftPanelIconsContainer = document.getElementById(
        'apps-bar-icons',
      ) as HTMLElement | null;
    }
    return this.leftPanelIconsContainer;
  }

  public getContextMenuState(
    selectedItem: fabric.Object | BoardItem,
    vpt: number[],
  ): FabricObjectState {
    const containerObject = this.getContainerObject(selectedItem);
    // calculate axis-aligned bounding box in screen (viewport) coordinates based on rotation
    const objectDataMappedToScreen = this.mapObjectToScreenSpace(containerObject, vpt);
    const { center, rotatedWidth, rotatedHeight } = objectDataMappedToScreen;
    // add a small lift for near-vertical orientations to avoid overlapping the rotation control
    const normalizedAngle = (((containerObject.angle ?? 0) % 360) + 360) % 360;
    const topOffsetPx = this.getTopOffsetPx(normalizedAngle);
    // same case for the images with vertical context menu
    const rightOffsetPx = this.getRightOffsetPx(selectedItem, normalizedAngle);

    // prioritize to render the context menu above the object
    let top = center.y - rotatedHeight / 2 - topOffsetPx;
    const left = center.x;
    const isObjectInViewport = this.sharedDataService.fabricCanvas
      ? this.isObjectInViewport(containerObject, this.sharedDataService.fabricCanvas, vpt)
      : false;

    if (this.shouldRenderMenuOnRight(selectedItem)) {
      const width = rotatedWidth + rightOffsetPx;
      return {
        top,
        left: left + width / 2 + MENU_OFFSET_FROM_IMAGE_PDF,
        width,
        height: rotatedHeight,
        renderMenu: isObjectInViewport,
      };
    }
    this.isMenuRenderedOnTop = this.shouldRenderMenuOnTop(top);
    if (!this.isMenuRenderedOnTop) {
      // for rotation control to be visible
      const bottomOffsetPx = this.getBottomOffsetPx(normalizedAngle);
      top = center.y + rotatedHeight / 2 + bottomOffsetPx;
    }
    top = this.adjustTopToBottomBar(top);
    this.lastContextMenuTopY = top;
    this.setColorPickerTopPosition();

    return {
      top: top + this.getMenuOffsetFromObject(),
      left,
      width: rotatedWidth + rightOffsetPx,
      height: rotatedHeight,
      renderTop: this.isMenuRenderedOnTop,
      translateX: this.calculateHorizontalTranslatePercent(
        selectedItem,
        left,
        top,
        this.sharedDataService.fabricCanvas,
        vpt,
      ),
      renderMenu: isObjectInViewport,
    };
  }

  private isPDF(selectedItem: fabric.Object | BoardItem): boolean {
    const isOldPDF = (selectedItem as BoardItem).itemType === ItemModel.PDF;
    return isBoardImagesPdf(selectedItem) || isOldPDF;
  }

  private shouldRenderMenuOnRight(selectedItem: fabric.Object | BoardItem): boolean {
    return isBoardImage(selectedItem) || this.isPDF(selectedItem) || isBoardVideo(selectedItem);
  }

  // in case of sticky note in edit mode, the context menu should render according to the position of the parent board item
  private getContainerObject(itemSelected: fabric.Object | BoardItem) {
    if (isBoardStickyNoteTextBox(itemSelected)) {
      return (itemSelected as any).parentBoardItem;
    }
    return itemSelected;
  }

  // The angles used represent the positions where context menu renders over the rotation control icon
  // We interpolated these formulas because the angle range is wide, and a fixed value is not suitable for all angles
  // NB: all these calculations depends on the current position of rotation control (right-top)
  private getTopOffsetPx(normalizedAngle: number): number {
    // To get these values we need 3 points: (angle, offset)
    // - By trials, we found the starting and ending angles where the menu hides the rotation control icon
    // - For the third point, we used the middle angle of the range (180-340)
    // - The offset for the middle point is max (30px) and for the other two points is (5px)
    if (normalizedAngle >= 180 && normalizedAngle <= 340) {
      return this.evaluateQuadratic(normalizedAngle, -0.00390625, 2.03125, -230);
    }
    return 0;
  }

  private getBottomOffsetPx(normalizedAngle: number): number {
    if (normalizedAngle > 0 && normalizedAngle <= 155) {
      return this.evaluateQuadratic(normalizedAngle, -0.00416667, 0.645833, 10);
    }
    return 0;
  }

  private evaluateQuadratic(angle: number, a: number, b: number, c: number): number {
    const angleSquared = angle * angle;
    return a * angleSquared + b * angle + c;
  }

  private getRightOffsetPx(
    selectedItem: fabric.Object | BoardItem,
    normalizedAngle: number,
  ): number {
    const isImage = isBoardImage(selectedItem);
    const needsRightShift =
      isImage &&
      ((normalizedAngle <= 75 && normalizedAngle >= 0) ||
        (normalizedAngle <= 365 && normalizedAngle >= 355));
    return needsRightShift ? 40 : 0;
  }

  private shouldRenderMenuOnTop(top: number): boolean {
    const y = top - CONTEXT_MENU_HEIGHT;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return y >= 0 && y <= viewportHeight;
  }

  // set the top position of the color picker based on the position of the context menu
  // to always be visible in the viewport
  private setColorPickerTopPosition() {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const y = this.lastContextMenuTopY - CONTEXT_MENU_HEIGHT - COLOR_PICKER_HEIGHT;
    const topMargin = -COLOR_PICKER_HEIGHT;
    const bottomMargin = COLOR_PICKER_RENDER_BOTTOM_OFFSET;
    if (y >= 0 && y <= viewportHeight) {
      this.colorPickerTopSubject.next(`${topMargin}px`);
    } else {
      this.colorPickerTopSubject.next(`${bottomMargin}px`);
    }
  }

  // Adjust the top position of menu to ensure it does not overlap with the bottom bar
  private adjustTopToBottomBar(top: number): number {
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    if (this.isMenuRenderedOnTop && top - CONTEXT_MENU_HEIGHT >= viewportHeight - BOTTOM_MARGIN) {
      return viewportHeight - BOTTOM_MARGIN + CONTEXT_MENU_HEIGHT;
    }
    if (!this.isMenuRenderedOnTop && top >= viewportHeight - BOTTOM_MARGIN) {
      return viewportHeight - BOTTOM_MARGIN;
    }
    return top;
  }

  // the offset is calculated based on the position of the menu relative to the object
  private getMenuOffsetFromObject(): number {
    if (this.isMenuRenderedOnTop) {
      return CONTEXT_MENU_RENDER_TOP_OFFSET * -1;
    }
    return CONTEXT_MENU_RENDER_BOTTOM_OFFSET;
  }

  private getViewportBounds(canvas: fabric.Canvas, vpt: number[]) {
    const zoom = canvas.getZoom();

    const x = -vpt[4] / zoom;
    const y = -vpt[5] / zoom;
    const width = this.panelLayoutService.getVisibleCanvasWidth(canvas?.width ?? 0);
    const height = (canvas?.height ?? 0) - TOP_NAV_BAR_HEIGHT;

    return {
      left: x,
      top: y,
      right: x + width / zoom,
      bottom: y + height / zoom,
      width,
    };
  }

  private isObjectInViewport(object: fabric.Object, canvas: fabric.Canvas, vpt: number[]) {
    const rect = object.getBoundingRect(true, true);
    const vp = this.getViewportBounds(canvas, vpt);

    return !(
      rect.left > vp.right ||
      rect.left + rect.width < vp.left ||
      rect.top > vp.bottom ||
      rect.top + rect.height < vp.top
    );
  }

  private calculateHorizontalTranslatePercent(
    item: fabric.Object | BoardItem,
    centerX: number,
    top: number,
    canvas: fabric.Canvas | undefined,
    vpt: number[],
  ): number {
    const menuWidth = this.getMenuWidthForItem(item);
    if (menuWidth <= 0 || !canvas) {
      return -50; // default center
    }

    const halfWidth = menuWidth / 2;
    let leftForMenu = centerX - halfWidth;
    const rightForMenu = centerX + halfWidth;
    const topForMenu = top;

    const vp = this.getViewportBounds(canvas, vpt);
    let viewportWidth = vp.width;
    // Video Call Tower support
    const videoTower = this.getVCTowerContainerEl();
    const vctRect = videoTower?.getBoundingClientRect();
    if (
      vctRect &&
      this.isMenuIntersectingWithElement(videoTower, leftForMenu, topForMenu, rightForMenu)
    ) {
      viewportWidth -= vctRect?.width + MENU_MARGIN_FROM_VCT;
    }
    const leftPanelIcons = this.getLeftPanelIconsContainerEl();
    if (this.isMenuIntersectingWithElement(leftPanelIcons, leftForMenu, topForMenu)) {
      leftForMenu -= LEFT_PANEL_ICONS_WIDTH;
    }

    const overflowLeft = Math.max(0, -leftForMenu);
    const overflowRight = Math.max(0, rightForMenu - viewportWidth);

    let translatePercent = -50; // center
    if (overflowLeft > 0) {
      translatePercent +=
        (overflowLeft / menuWidth) * 100 + EXTRA_MARGIN_FOR_MENU_TRANSLATE_IN_PERCENT; // shift right
    } else if (overflowRight > 0) {
      translatePercent -=
        (overflowRight / menuWidth) * 100 + EXTRA_MARGIN_FOR_MENU_TRANSLATE_IN_PERCENT; // shift left
    }

    return translatePercent;
  }

  // right is optional and used for video call tower intersection for now
  private isMenuIntersectingWithElement(
    htmlElement: HTMLElement | null,
    left: number,
    top: number,
    right?: number,
  ): boolean {
    if (!htmlElement) {
      return false;
    }
    const rect = htmlElement?.getBoundingClientRect();
    if (!rect) {
      return false;
    }
    const verticalMargin = right ? 20 : 0;
    const horizontalIntersection = right
      ? rect?.right > left || rect?.left < right
      : rect?.right > left;
    const verticalIntersection = rect?.bottom + verticalMargin > top;
    return horizontalIntersection && verticalIntersection;
  }

  public shouldRenderSubMenuOnTop(popupMenuHeight: number): boolean {
    let subMenuTop = 0;
    if (this.isMenuRenderedOnTop) {
      subMenuTop = this.lastContextMenuTopY - CONTEXT_MENU_HEIGHT;
    } else {
      // no need to consider context menu height here as the popup menu renders at the same level
      subMenuTop = this.lastContextMenuTopY;
    }
    subMenuTop -= popupMenuHeight;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return subMenuTop >= 0 && subMenuTop <= viewportHeight;
  }

  private mapObjectToScreenSpace(object: fabric.Object, vpt: number[]): ObjectDataMappedToScreen {
    const center = object.getCenterPoint();
    const angleRad = ((object.angle ?? 0) * Math.PI) / 180;
    const scaledWidth = object.getScaledWidth();
    const scaledHeight = object.getScaledHeight();
    const rotatedWidth =
      Math.abs(scaledWidth * Math.cos(angleRad)) + Math.abs(scaledHeight * Math.sin(angleRad));
    const rotatedHeight =
      Math.abs(scaledWidth * Math.sin(angleRad)) + Math.abs(scaledHeight * Math.cos(angleRad));

    const objectCenterMappedToScreenX = vpt[0] * center.x + vpt[2] * center.y + vpt[4];
    const objectCenterMappedToScreenY = vpt[1] * center.x + vpt[3] * center.y + vpt[5];

    // account for current zoom when translating dimensions to screen space
    const zoomX = vpt[0];
    const zoomY = vpt[3];
    const rotatedWidthScreen = rotatedWidth * zoomX;
    const rotatedHeightScreen = rotatedHeight * zoomY;
    return {
      center: {
        x: objectCenterMappedToScreenX,
        y: objectCenterMappedToScreenY,
      },
      rotatedWidth: rotatedWidthScreen,
      rotatedHeight: rotatedHeightScreen,
    };
  }

  private getMenuWidthForItem(item: fabric.Object | BoardItem): number {
    if ((item as any)?.locked) {
      return this.menuTypeToWidth[MenuType.LOCKED];
    }
    if (this.isMultiSelection(item)) {
      // in case of multi selection, we just show the delete button
      return this.menuTypeToWidth[MenuType.MULTI_SELECTION];
    }
    let menuWidth = 0;

    if (this.flagService.isFlagEnabled(FLAGS.ENABLE_SPACES_COMMENTS)) {
      menuWidth = COMMENT_MENU_WIDTH;
    }

    if (isBoardStickyNote(item) || isBoardStickyNoteTextBox(item)) {
      return menuWidth + this.menuTypeToWidth[MenuType.STICKY_NOTE];
    }
    if (isBoardFormula(item)) {
      return menuWidth + this.menuTypeToWidth[MenuType.FORMULA];
    }
    if (item?.type === 'textbox' || item?.type === 'i-text') {
      return menuWidth + this.menuTypeToWidth[MenuType.TEXTBOX];
    }
    if (isBoardLine(item)) {
      return menuWidth + this.menuTypeToWidth[MenuType.LINE];
    }
    if (itemIsScribbleOrHighlight(item)) {
      return menuWidth + this.menuTypeToWidth[MenuType.SCRIBBLE];
    }
    if (isShape(item)) {
      return menuWidth + this.menuTypeToWidth[MenuType.SHAPE];
    }

    return 0;
  }

  private isMultiSelection(item: fabric.Object | BoardItem): boolean {
    return (item as any)?._objects && (item as any)?._objects.length > 1;
  }

  public cleanUp(): void {
    this.videoCallTowerSubscription.unsubscribe();
    this.videoCallTowerResizeObserver?.disconnect();
    this.videoCallTowerResizeObserver = undefined;
    this.videoCallTowerContainer = null;
    this.leftPanelIconsContainer = null;
  }
}
